import { JSX } from 'react';
import { InfoIcon } from '@chakra-ui/icons';
import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  OrderedList,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import { useHtmlTranslations } from '../../../contexts/LocalizationContext';
import { getQRCodeSrc } from '../../../utils/qrCodesHelper';

const HandScannerHelp = (): JSX.Element => {
  const t = useHtmlTranslations();
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <Button color="grey.900" onClick={onOpen} variant="link" leftIcon={<Icon as={InfoIcon} />} size="sm" pl={0}>
        {t('hand_scanner_help_cta')}
      </Button>

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
        <ModalOverlay />
        <ModalContent display="flex" flexDirection="column" maxH="85vh">
          <ModalHeader>{t('hand_scanner_help_title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pt={2} overflowY="auto">
            <VStack align="start" spacing={4} color="grey.900">
              <Text>{t('hand_scanner_help_intro')}</Text>

              {/* Step 1 */}
              <Box width="100%">
                <Text fontWeight="semibold" mb={1}>
                  {t('hand_scanner_help_step_1')}
                </Text>
                <Text>{t('hand_scanner_help_step_1_description')}</Text>
                <Flex mt={3} height="256px" width="100%" justifyContent="center">
                  <Image src={getQRCodeSrc('%%SpecCodeAC')} alt="QR Code" boxSize="256px" />
                </Flex>
              </Box>

              {/* Step 2 */}
              <Box width="100%">
                <Text fontWeight="semibold" mb={1}>
                  {t('hand_scanner_help_step_2')}
                </Text>
              </Box>

              {/* Step 3 */}
              <Box width="100%">
                <Text fontWeight="semibold" mb={1}>
                  {t('hand_scanner_help_step_3')}
                </Text>
                <Text mb={2}>
                  {t('hand_scanner_help_step_3_description', {
                    '{{bold_start}}': '<strong>',
                    '{{bold_end}}': '</strong>',
                  })}
                </Text>
                <OrderedList spacing={1} pl={5} stylePosition="outside">
                  <ListItem>
                    {t('hand_scanner_help_step_3_list_1', {
                      '{{bold_start}}': '<strong>',
                      '{{bold_end}}': '</strong>',
                    })}
                  </ListItem>
                  <ListItem>{t('hand_scanner_help_step_3_list_2')}</ListItem>
                  <ListItem>
                    {t('hand_scanner_help_step_3_list_3', {
                      '{{bold_start}}': '<strong>',
                      '{{bold_end}}': '</strong>',
                    })}
                  </ListItem>
                  <ListItem>
                    {t('hand_scanner_help_step_3_list_4', {
                      '{{bold_start}}': '<strong>',
                      '{{bold_end}}': '</strong>',
                    })}
                  </ListItem>
                </OrderedList>
              </Box>

              {/* Additional Mac-specific note (no OS-based sections) */}
              <Box width="100%">
                <Text mb={2}>
                  {t('hand_scanner_help_mac_description', {
                    '{{bold_start}}': '<strong>',
                    '{{bold_end}}': '</strong>',
                  })}
                </Text>
                <OrderedList stylePosition="outside" pl={5}>
                  <ListItem>{t('hand_scanner_help_mac_list_1')}</ListItem>
                  <ListItem>{t('hand_scanner_help_mac_list_2')}</ListItem>
                  <ListItem>{t('hand_scanner_help_mac_list_3')}</ListItem>
                </OrderedList>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose} variant="orangeSolid" width="100%">
              {t('hand_scanner_help_close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default HandScannerHelp;
